<template>
  <div>
    <form-wrapper @submit="onSubmit">
      <div slot="box" v-loading="loading">
        <component-compiler
          v-for="(component, i) in getFields()"
          :content="component"
          :key="`form-${i}`"
        />
      </div>
      <form-nav
        :loading="loading"
        slot="navbar"
        @submit="onSubmit"
        :redirectRoute="redirectRoute"
        :hideLanguageSwitcher="true"
        :subtitle="`Status: ${form.status_display || ''}`"
        :titleAddition="studentFullName"
      >
        <div slot="middle" class="otp-code-controls">
          <el-button
            :disabled="loading || otpCodeRequestLoading || !otpCodeApplicantId"
            :loading="otpCodeRequestLoading"
            @click="generateOtpCode"
          >
            Generate verification code
          </el-button>
          <div class="otp-code-group">
            <el-input
              v-model="otpCode"
              type="text"
              :class="{
                'generate-button': true,
                'generate-button-expired': otpCodeIsExpired
              }"
              readonly
            />
            <span v-if="otpCodeExpiresIn" class="otp-code-expiry">
              {{ otpCodeExpiresIn }}
            </span>
          </div>
        </div>
        <component-compiler
          v-for="(component, i) in getNavButtons()"
          :content="component"
          :key="`form-${i}`"
        />
      </form-nav>
    </form-wrapper>
    <el-dialog
      :title="dialogTitle"
      append-to-body
      width="50vw"
      top="25vh"
      :visible.sync="dialogVisible"
    >
      <wysiwyg-input v-if="dialogVisible" v-model="comment" simple />
      <div v-show="commentError" class="comment-error">
        {{ commentError }}
      </div>
      <p>Attachment</p>
      <attachment-input
        v-if="dialogVisible"
        v-model="attachment"
        keepOriginalFilename
      />
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button @click="makeDecision()" type="primary">Submit</el-button>
      </span>
    </el-dialog>
  </div>
</template>
<script>
import { Application, User } from '@/api'
import FormMixin from '@/crudBases/form/FormMixin'
import fields from '@/components/fields/'
import { get } from 'lodash'
import moment from 'moment'

export default {
  mixins: [FormMixin],
  data() {
    return {
      redirectRoute: { name: 'Application List' },
      form: {
        last_schools: [],
        studied_languages: [],
        awards: [],
        favourite_subjects: [],
        least_favourite_subjects: [],
        musical_instruments: [],
        medical_reports: [],
        report_cards_from_last_three_years: [],
        rosenberg_legacies: [],
        allergies: [],
        chronic_conditions: [],
        medications: [],
        mental_health_issues: [],
        personal_statement: [],
        student_passport_photo: [],
        legal_guardian_passport_photo: [],
        vaccination_record: [],
        other_document: []
      },
      choices: {},
      comment: '',
      attachment: null,
      commentError: '',
      dialogVisible: false,
      decisionService: null,
      otpCodeNow: Date.now(),
      otpCodeRequestLoading: false,
      otpCodeTimerId: null,
      otpCode: '',
      otpCodeExpiresAt: null
    }
  },
  mounted() {
    this.otpCodeTimerId = setInterval(() => {
      this.otpCodeNow = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.otpCodeTimerId)
  },
  computed: {
    studentFullName() {
      const firstName = get(this.form, 'student.first_name', '')
      const lastName = get(this.form, 'student.last_name', '')
      return `- ${firstName} ${lastName}`
    },
    service() {
      return Application
    },
    dialogTitle() {
      return this.decisionService === this.service.markApproved
        ? 'Approval message'
        : 'Rejection message'
    },
    otpCodeApplicantId() {
      return get(this.form, 'student.applicant.id', null)
    },
    otpCodeIsExpired() {
      if (!this.otpCodeExpiresAt) {
        return false
      }
      return moment(this.otpCodeExpiresAt).isBefore(moment(this.otpCodeNow))
    },
    otpCodeExpiresIn() {
      if (!this.otpCodeExpiresAt) {
        return ''
      }
      const expiryMoment = moment(this.otpCodeExpiresAt)
      if (this.otpCodeIsExpired) {
        return `expired ${expiryMoment.fromNow()}`
      }
      return `expires ${expiryMoment.fromNow()}`
    },
    showLegalGuardianFields() {
      return !['first_parent', 'second_parent', 'both_parents'].includes(
        this.form.legal_guardian_relation
      )
    }
  },
  methods: {
    instanceFetchCallback() {
      this.prepareFormCopyToPreventChangesLost()
      const otpCodeData = get(this.form, 'otp_code_data')
      this.otpCode = get(otpCodeData, 'code', '')
      this.otpCodeExpiresAt = get(otpCodeData, 'expires_at', null)
    },
    fetchDataCallback() {
      this.service.choices().then(response => {
        this.choices = response.data
      })
    },
    getFields() {
      const tabs = [
        this.getApplicantTab(),
        this.getStudentTab(),
        this.getParentsTab(),
        this.getAcademicGoalsTab(),
        this.getAcademicHistoryTab(),
        this.getRosenbergLegacyTab(),
        this.getInterestsTab(),
        this.getMedicalInformationTab(),
        this.getDocumentsTab()
      ]
      if (this.form.decision_comment) {
        tabs.push(this.getDecisionCommentTab())
      }
      return [
        {
          component: 'el-tabs',
          children: tabs
        }
      ]
    },
    getApplicantTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Applicant information'
        },
        children: [
          new fields.Select({
            label: 'Relation',
            modelPath: [this, 'form', 'applicant_relation'],
            options: get(this.choices, 'applicant_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'student', 'applicant', 'first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'student', 'applicant', 'last_name'],
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'applicant_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'applicant_salutation_other'],
            condition: ['title', 'other'].includes(
              get(this.form, 'applicant_salutation')
            ),
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'applicant_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'student', 'applicant', 'email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'applicant_secondary_email'],
            disabled: true,
            condition: !!this.form.applicant_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'applicant_phone'],
            disabled: true
          })
        ]
      }
    },
    getStudentTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Student information'
        },
        children: [
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'student', 'first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Middle name',
            modelPath: [this, 'form', 'student', 'middle_name'],
            disabled: true,
            condition: get(this.form.student, 'middle_name')
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'student', 'last_name'],
            disabled: true
          }),
          new fields.Date({
            label: 'Birthdate',
            format: 'yyyy-MM-dd',
            modelPath: [this, 'form', 'student', 'birthdate'],
            disabled: true
          }),
          new fields.Input({
            label: 'Birthplace',
            modelPath: [this, 'form', 'student', 'birthplace'],
            disabled: true
          }),
          new fields.Select({
            label: 'Gender',
            modelPath: [this, 'form', 'student', 'gender'],
            options: get(this.choices, 'gender', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Address',
            modelPath: [this, 'form', 'student', 'address'],
            disabled: true
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'student', 'address_city'],
            disabled: true
          }),
          new fields.Input({
            label: 'Zipcode',
            modelPath: [this, 'form', 'student', 'address_zipcode'],
            disabled: true
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'student', 'address_country'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is billing address same',
            modelPath: [this, 'form', 'student', 'is_billing_address_same'],
            disabled: true
          }),
          new fields.Header({
            text: !get(this.form, 'student.is_billing_address_same')
              ? 'Billing address'
              : '',
            type: 'h4'
          }),
          new fields.Input({
            label: 'Billing address',
            modelPath: [this, 'form', 'student', 'billing_address'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'City',
            modelPath: [this, 'form', 'student', 'billing_address_city'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'Zipcode',
            modelPath: [this, 'form', 'student', 'billing_address_zipcode'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          }),
          new fields.Input({
            label: 'Country',
            modelPath: [this, 'form', 'student', 'billing_address_country'],
            disabled: true,
            condition: !get(this.form, 'student.is_billing_address_same')
          })
        ]
      }
    },
    getParentsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Parents & legal guardian'
        },
        children: [
          new fields.Header({
            text: 'Parent 1',
            type: 'h4'
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'parent_1_first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'parent_1_last_name'],
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'parent_1_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'parent_1_salutation_other'],
            condition: get(this.form, 'parent_1_salutation') === 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'parent_1_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'parent_1_email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'parent_1_secondary_email'],
            disabled: true,
            condition: !!this.form.parent_1_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'parent_1_phone'],
            disabled: true
          }),
          new fields.Header({
            text: 'Parent 2',
            type: 'h4',
            condition: !!this.form.parent_2_first_name
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'parent_2_first_name'],
            disabled: true,
            condition: !!this.form.parent_2_first_name
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'parent_2_last_name'],
            disabled: true,
            condition: !!this.form.parent_2_last_name
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'parent_2_salutation'],
            options: get(this.choices, 'salutation', []),
            disabled: true,
            condition: !!this.form.parent_2_salutation
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'parent_2_salutation_other'],
            condition: get(this.form, 'parent_2_salutation') === 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'parent_2_occupation'],
            disabled: true,
            condition: !!this.form.parent_2_occupation
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'parent_2_email'],
            disabled: true,
            condition: !!this.form.parent_2_email
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'parent_2_secondary_email'],
            disabled: true,
            condition: !!this.form.parent_2_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'parent_2_phone'],
            disabled: true,
            condition: !!this.form.parent_2_phone
          }),
          new fields.Header({
            text: 'Legal guardian',
            type: 'h4'
          }),
          new fields.Select({
            label: 'Relation',
            modelPath: [this, 'form', 'legal_guardian_relation'],
            options: get(this.choices, 'legal_guardian_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'legal_guardian_first_name'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'legal_guardian_last_name'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Select({
            label: 'Salutation',
            modelPath: [this, 'form', 'legal_guardian_salutation'],
            options: get(this.choices, 'salutation', []),
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Salutation other',
            modelPath: [this, 'form', 'legal_guardian_salutation_other'],
            condition:
              this.showLegalGuardianFields &&
              get(this.form, 'legal_guardian_salutation') === 'other',
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'legal_guardian_occupation'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'legal_guardian_email'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'legal_guardian_secondary_email'],
            condition:
              this.showLegalGuardianFields &&
              !!this.form.legal_guardian_secondary_email,
            disabled: true
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'legal_guardian_phone'],
            condition: this.showLegalGuardianFields,
            disabled: true
          }),
          new fields.Header({
            text: 'Contact in legal guardian’s absence',
            type: 'h4'
          }),
          new fields.Input({
            label: 'First name',
            modelPath: [this, 'form', 'contact_first_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Last name',
            modelPath: [this, 'form', 'contact_last_name'],
            disabled: true
          }),
          new fields.Input({
            label: 'Occupation',
            modelPath: [this, 'form', 'contact_occupation'],
            disabled: true
          }),
          new fields.Input({
            label: 'Email',
            modelPath: [this, 'form', 'contact_email'],
            disabled: true
          }),
          new fields.Input({
            label: 'Secondary email',
            modelPath: [this, 'form', 'contact_secondary_email'],
            disabled: true,
            condition: !!this.form.contact_secondary_email
          }),
          new fields.Input({
            label: 'Phone',
            modelPath: [this, 'form', 'contact_phone'],
            disabled: true
          }),
          new fields.Select({
            label: 'Child relation',
            modelPath: [this, 'form', 'contact_child_relation'],
            options: get(this.choices, 'parent_relation', []),
            disabled: true
          }),
          new fields.Input({
            label: 'Child relation other',
            modelPath: [this, 'form', 'contact_child_relation_other'],
            condition: get(this.form, 'contact_child_relation') === 'other',
            disabled: true
          }),
          new fields.YesNo({
            label: 'Allowed to visit',
            modelPath: [this, 'form', 'is_contact_allowed_to_visit'],
            disabled: true
          })
        ]
      }
    },
    getAcademicGoalsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Academic goals'
        },
        children: [
          new fields.Input({
            label: 'Date of entry to rosenberg (year)',
            modelPath: [this, 'form', 'date_of_entry_to_rosenberg_year'],
            disabled: true
          }),
          new fields.Input({
            label: 'Career aim',
            modelPath: [this, 'form', 'career_aim'],
            type: 'textarea',
            disabled: true
          }),
          new fields.Select({
            label:
              'Which type of subject would the student like to study after graduation?',
            modelPath: [this, 'form', 'mission_control_degree_1_aim'],
            options: get(this.choices, 'degree_aims', []),
            disabled: true
          }),
          new fields.Select({
            label: '',
            modelPath: [this, 'form', 'mission_control_degree_2_aim'],
            options: get(this.choices, 'degree_aims', []),
            disabled: true
          }),
          new fields.Select({
            label:
              'Which country(s) does the student want to study in after graduation?',
            modelPath: [this, 'form', 'mission_control_degree_1_destination'],
            options: get(this.choices, 'degree_destinations', []),
            disabled: true
          }),
          new fields.Select({
            label: '',
            modelPath: [this, 'form', 'mission_control_degree_2_destination'],
            options: get(this.choices, 'degree_destinations', []),
            disabled: true
          })
        ]
      }
    },
    getAcademicHistoryTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Academic history'
        },
        children: [
          new fields.Header({
            text: 'Last schools',
            type: 'h4',
            condition: this.form.last_schools.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'last_schools'],
            readonly: true,
            noItemsText: '',
            items: this.form.last_schools.map((item, i) => {
              return {
                headerLabel: `School ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'School name',
                    modelPath: [this, 'form', 'last_schools', i, 'school_name'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'From (month)',
                    modelPath: [
                      this,
                      'form',
                      'last_schools',
                      i,
                      'school_from_month'
                    ],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'From (year)',
                    modelPath: [
                      this,
                      'form',
                      'last_schools',
                      i,
                      'school_from_year'
                    ],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Until (month)',
                    modelPath: [
                      this,
                      'form',
                      'last_schools',
                      i,
                      'school_until_month'
                    ],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Until (year)',
                    modelPath: [
                      this,
                      'form',
                      'last_schools',
                      i,
                      'school_until_year'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Studied languages',
            type: 'h4',
            condition: this.form.studied_languages.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'studied_languages'],
            readonly: true,
            noItemsText: '',
            items: this.form.studied_languages.map((item, i) => {
              return {
                headerLabel: `Language ${i + 1}`,
                fields: [
                  new fields.Select({
                    label: 'Language',
                    modelPath: [
                      this,
                      'form',
                      'studied_languages',
                      i,
                      'language_name'
                    ],
                    options: get(this.choices, 'language_name', []),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Language other',
                    modelPath: [
                      this,
                      'form',
                      'studied_languages',
                      i,
                      'language_name_other'
                    ],
                    condition: get(item, 'language_name') === 'other',
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Level',
                    modelPath: [
                      this,
                      'form',
                      'studied_languages',
                      i,
                      'language_level'
                    ],
                    options: get(this.choices, 'language_level', []),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Level other',
                    modelPath: [
                      this,
                      'form',
                      'studied_languages',
                      i,
                      'language_level_other'
                    ],
                    condition: get(item, 'language_level') === 'other',
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Year',
                    modelPath: [
                      this,
                      'form',
                      'studied_languages',
                      i,
                      'language_year'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Any other language qualifications',
            modelPath: [this, 'form', 'has_other_language_qualifications'],
            disabled: true
          }),
          new fields.Input({
            label: 'Other language qualifications',
            modelPath: [this, 'form', 'other_language_qualifications'],
            type: 'textarea',
            condition: this.form.has_other_language_qualifications,
            disabled: true
          })
        ]
      }
    },
    getRosenbergLegacyTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Rosenberg legacy'
        },
        children: [
          new fields.Header({
            text: 'How did student know about Rosenberg',
            type: 'h4',
            condition: this.form.rosenberg_legacies.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'rosenberg_legacies'],
            readonly: true,
            noItemsText: '',
            items: this.form.rosenberg_legacies.map((item, i) => {
              return {
                headerLabel: get(item, ['legacy_type_display']),
                fields: [
                  new fields.Input({
                    label: 'First name',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'first_name'
                    ],
                    condition: !['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Last name',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'last_name'
                    ],
                    condition: !['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Other',
                    modelPath: [
                      this,
                      'form',
                      'rosenberg_legacies',
                      i,
                      'other_comment'
                    ],
                    condition: ['educational_consultant', 'other'].includes(
                      get(item, 'legacy_type')
                    ),
                    type: 'textarea',
                    disabled: true
                  })
                ]
              }
            })
          })
        ]
      }
    },
    getInterestsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Interests'
        },
        children: [
          new fields.Input({
            label: 'Current hobbies',
            modelPath: [this, 'form', 'current_hobbies'],
            type: 'textarea',
            disabled: true
          }),
          new fields.Input({
            label: 'Current interests',
            modelPath: [this, 'form', 'current_interests'],
            type: 'textarea',
            disabled: true
          }),
          new fields.Input({
            label: 'Would have lunch with',
            modelPath: [this, 'form', 'would_have_to_lunch_with'],
            type: 'textarea',
            disabled: true
          }),
          new fields.Header({
            text: 'Awards',
            type: 'h4',
            condition: this.form.awards.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'awards'],
            readonly: true,
            noItemsText: '',
            items: this.form.awards.map((item, i) => {
              return {
                headerLabel: `Award ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Award name',
                    modelPath: [this, 'form', 'awards', i, 'award_name'],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Favourite subjects',
            type: 'h4',
            condition: this.form.favourite_subjects.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'favourite_subjects'],
            readonly: true,
            noItemsText: '',
            items: this.form.favourite_subjects.map((item, i) => {
              return {
                headerLabel: `Subject ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Subject name',
                    modelPath: [
                      this,
                      'form',
                      'favourite_subjects',
                      i,
                      'subject_name'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Least favourite subjects',
            type: 'h4'
          }),
          new fields.YesNo({
            label: 'Any least favourite subjects',
            modelPath: [this, 'form', 'has_any_least_favourite_subjects'],
            disabled: true
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'least_favourite_subjects'],
            readonly: true,
            noItemsText: '',
            items: this.form.least_favourite_subjects.map((item, i) => {
              return {
                headerLabel: `Subject ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Subject name',
                    modelPath: [
                      this,
                      'form',
                      'least_favourite_subjects',
                      i,
                      'subject_name'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Input({
            label: 'Last book read',
            modelPath: [this, 'form', 'last_book_read'],
            disabled: true
          }),
          new fields.Header({
            text: 'Musical instruments',
            type: 'h4'
          }),
          new fields.YesNo({
            label: 'Any musical instruments',
            modelPath: [
              this,
              'form',
              'does_student_play_any_musical_instruments'
            ],
            disabled: true
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'musical_instruments'],
            readonly: true,
            noItemsText: '',
            items: this.form.musical_instruments.map((item, i) => {
              return {
                headerLabel: `Instrument ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Instrument name',
                    modelPath: [
                      this,
                      'form',
                      'musical_instruments',
                      i,
                      'instrument_name'
                    ],
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Instrument level',
                    modelPath: [
                      this,
                      'form',
                      'musical_instruments',
                      i,
                      'instrument_level'
                    ],
                    options: get(this.choices, 'instrument_level', []),
                    disabled: true
                  })
                ]
              }
            })
          })
        ]
      }
    },
    getMedicalInformationTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Medical information'
        },
        children: [
          new fields.YesNo({
            label: 'Does the student have any allergies?',
            modelPath: [this, 'form', 'has_any_allergies'],
            disabled: true
          }),
          new fields.Header({
            text: 'Allergies',
            type: 'h4',
            condition: this.form.allergies.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'allergies'],
            readonly: true,
            noItemsText: '',
            blankObject: { reactions: [] },
            items: this.form.allergies.map((item, i) => {
              return {
                headerLabel: `Allergy ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Allergen type',
                    modelPath: [this, 'form', 'allergies', i, 'allergen_type'],
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Allergen subtypes',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'allergies',
                      i,
                      'allergen_subtypes'
                    ],
                    options: [],
                    disabled: true
                  }),
                  new fields.Header({
                    text: 'Reactions',
                    type: 'h4',
                    condition: this.form.allergies[i].reactions.length
                  }),
                  this.getAllergyReactionsFormSet(
                    this.form.allergies[i].reactions,
                    i
                  )
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any chronic conditions?',
            modelPath: [this, 'form', 'has_any_chronic_conditions'],
            disabled: true
          }),
          new fields.Header({
            text: 'Chronic conditions',
            type: 'h4',
            condition: this.form.chronic_conditions.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'chronic_conditions'],
            readonly: true,
            noItemsText: '',
            items: this.form.chronic_conditions.map((item, i) => {
              return {
                headerLabel: `Chronic condition ${i + 1}`,
                fields: [
                  new fields.Select({
                    label: 'Chronic condition type',
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'condition_type'
                    ],
                    options: get(this.choices, 'condition_types', []),
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Other condition type',
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'other_condition_type'
                    ],
                    condition: get(item, 'condition_type') === 'other',
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Subtype',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'condition_subtype'
                    ],
                    options: [],
                    disabled: true
                  }),
                  new fields.Select({
                    label: 'Impact on daily routine',
                    multiple: true,
                    modelPath: [
                      this,
                      'form',
                      'chronic_conditions',
                      i,
                      'impact'
                    ],
                    options: get(this.choices, 'impacts_on_daily_routine', []),
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any dietary restrictions?',
            modelPath: [this, 'form', 'has_any_dietary_restrictions'],
            disabled: true
          }),
          new fields.Input({
            label: 'Dietary restrictions',
            modelPath: [this, 'form', 'dietary_restrictions'],
            condition: this.form.any_dietary_restrictions,
            disabled: true
          }),
          new fields.YesNo({
            label: 'Does the student take any regular medication?',
            modelPath: [this, 'form', 'has_any_regular_medication'],
            disabled: true
          }),
          new fields.Header({
            text: 'Medications',
            type: 'h4',
            condition: this.form.medications.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'medications'],
            readonly: true,
            noItemsText: '',
            items: this.form.medications.map((item, i) => {
              return {
                headerLabel: `Medication ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Name of medication',
                    modelPath: [
                      this,
                      'form',
                      'medications',
                      i,
                      'medicine_name'
                    ],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Daily dose (mg)',
                    modelPath: [this, 'form', 'medications', i, 'dosage'],
                    disabled: true
                  }),
                  new fields.Input({
                    label: 'Reason and diagnosis',
                    modelPath: [
                      this,
                      'form',
                      'medications',
                      i,
                      'reason_and_diagnosis'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Mental health issues',
            type: 'h4',
            condition: this.form.mental_health_issues.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'mental_health_issues'],
            readonly: true,
            noItemsText: '',
            items: this.form.mental_health_issues.map((item, i) => {
              return {
                headerLabel: `Mental health issue ${i + 1}`,
                fields: [
                  new fields.Input({
                    label: 'Name',
                    modelPath: [
                      this,
                      'form',
                      'mental_health_issues',
                      i,
                      'issue_name'
                    ],
                    disabled: true
                  })
                ]
              }
            })
          }),
          new fields.YesNo({
            label: 'Does the student have any mental health issues?',
            modelPath: [this, 'form', 'has_any_mental_health_issues'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has regular psychological care',
            modelPath: [this, 'form', 'has_regular_psychological_care'],
            disabled: true
          }),
          new fields.Header({
            text: 'Medical reports',
            type: 'h4',
            condition: this.form.medical_reports.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'medical_reports'],
            readonly: true,
            noItemsText: '',
            items: this.form.medical_reports.map((item, i) => {
              return {
                headerLabel: `Medical report ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    modelPath: [this, 'form', 'medical_reports', i, 'file']
                  })
                ]
              }
            })
          }),
          new fields.Input({
            label: 'Height',
            modelPath: [this, 'form', 'student_height'],
            disabled: true
          }),
          new fields.Input({
            label: 'Weight',
            modelPath: [this, 'form', 'student_weight'],
            disabled: true
          }),
          new fields.Select({
            label: "Student's fitness for sports",
            modelPath: [this, 'form', 'student_fitness_for_sports'],
            options: get(this.choices, 'fitness_for_sports', []),
            disabled: true
          }),
          new fields.Select({
            label: "Student's blood type",
            modelPath: [this, 'form', 'student_blood_type'],
            options: get(this.choices, 'blood_type', []),
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is student swimmer',
            modelPath: [this, 'form', 'is_student_swimmer'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Is more medical information available',
            modelPath: [this, 'form', 'is_more_medical_information_available'],
            disabled: true
          }),
          new fields.Input({
            label: 'More medical information',
            modelPath: [this, 'form', 'more_medical_information'],
            condition: this.form.is_more_medical_information_available,
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has student ever been in contact with drugs',
            modelPath: [this, 'form', 'has_student_contact_with_drugs'],
            disabled: true
          }),
          new fields.YesNo({
            label: 'Has student ever been expelled',
            modelPath: [this, 'form', 'has_been_student_expelled'],
            disabled: true
          })
        ]
      }
    },
    getDocumentsTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Documents'
        },
        children: [
          new fields.Header({
            text: 'Report cards from last three years',
            type: 'h4',
            condition: this.form.report_cards_from_last_three_years.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'report_cards_from_last_three_years'],
            readonly: true,
            noItemsText: '',
            items: this.form.report_cards_from_last_three_years.map(
              (item, i) => {
                return {
                  headerLabel: `Report card ${i + 1}`,
                  fields: [
                    new fields.AttachmentPreview({
                      modelPath: [
                        this,
                        'form',
                        'report_cards_from_last_three_years',
                        i,
                        'file'
                      ]
                    })
                  ]
                }
              }
            )
          }),
          new fields.Header({
            text: 'Personal statement',
            type: 'h4',
            condition: this.form.personal_statement.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'personal_statement'],
            readonly: true,
            noItemsText: '',
            items: this.form.personal_statement.map((item, i) => {
              return {
                headerLabel: `Personal statement ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Personal statement',
                    modelPath: [this, 'form', 'personal_statement', i, 'file']
                  })
                ]
              }
            })
          }),
          new fields.AttachmentPreview({
            label: 'Student photo in passport format',
            modelPath: [
              this,
              'form',
              'student_photo_in_passport_format',
              'file'
            ]
          }),
          new fields.Header({
            text: 'Student passport photo',
            type: 'h4',
            condition: this.form.student_passport_photo.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'student_passport_photo'],
            readonly: true,
            noItemsText: '',
            items: this.form.student_passport_photo.map((item, i) => {
              return {
                headerLabel: `Student passport photo ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Student passport photo',
                    modelPath: [
                      this,
                      'form',
                      'student_passport_photo',
                      i,
                      'file'
                    ]
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Legal guardian passport photo',
            type: 'h4',
            condition: this.form.legal_guardian_passport_photo.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'legal_guardian_passport_photo'],
            readonly: true,
            noItemsText: '',
            items: this.form.legal_guardian_passport_photo.map((item, i) => {
              return {
                headerLabel: `Legal guardian passport photo ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Legal guardian passport photo',
                    modelPath: [
                      this,
                      'form',
                      'legal_guardian_passport_photo',
                      i,
                      'file'
                    ]
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Vaccination record',
            type: 'h4',
            condition: this.form.vaccination_record.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'vaccination_record'],
            readonly: true,
            noItemsText: '',
            items: this.form.vaccination_record.map((item, i) => {
              return {
                headerLabel: `Vaccination record ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Vaccination record',
                    modelPath: [this, 'form', 'vaccination_record', i, 'file']
                  })
                ]
              }
            })
          }),
          new fields.Header({
            text: 'Other document',
            type: 'h4',
            condition: this.form.other_document.length
          }),
          new fields.Formset({
            modelPath: [this, 'form', 'other_document'],
            readonly: true,
            noItemsText: '',
            items: this.form.other_document.map((item, i) => {
              return {
                headerLabel: `Other document ${i + 1}`,
                fields: [
                  new fields.AttachmentPreview({
                    label: 'Other document',
                    modelPath: [this, 'form', 'other_document', i, 'file']
                  })
                ]
              }
            })
          })
        ]
      }
    },
    getAllergyReactionsFormSet(reactions, allergy_index) {
      return new fields.Formset({
        modelPath: [this, 'form', 'allergies', allergy_index, 'reactions'],
        readonly: true,
        noItemsText: '',
        items: reactions.map((item, i) => {
          return {
            headerLabel: `Reaction ${i + 1}`,
            fields: [
              new fields.Input({
                label: 'Reaction name',
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'reaction'
                ],
                disabled: true
              }),
              new fields.Select({
                label: 'Reaction subtype (Severe)',
                multiple: true,
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'severe_reactions'
                ],
                options: [],
                disabled: true
              }),
              new fields.Select({
                label: 'Reaction subtype (Mild)',
                multiple: true,
                modelPath: [
                  this,
                  'form',
                  'allergies',
                  allergy_index,
                  'reactions',
                  i,
                  'mild_reactions'
                ],
                options: [],
                disabled: true
              })
            ]
          }
        })
      })
    },
    getDecisionCommentTab() {
      return {
        component: 'el-tab-pane',
        attrs: {
          label: 'Decision message'
        },
        children: [
          new fields.WYSIWYG({
            label: 'Message',
            modelPath: [this, 'form', 'decision_comment'],
            readonly: true,
            disabled: true
          }),
          new fields.AttachmentPreview({
            label: 'Attachment',
            modelPath: [this, 'form', 'decision_attachment', 'file'],
            showFileName: true
          })
        ]
      }
    },
    makeDecision() {
      this.decisionService(this.lookupParams, {
        decision_comment: this.comment,
        decision_attachment: this.attachment
      })
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
          this.commentError = ''
          this.dialogVisible = false
        })
        .catch(error => {
          if (error.response.data.non_field_errors) {
            this.$message({
              message: error.response.data.non_field_errors,
              type: 'error'
            })
          }
          this.commentError = error.response.data.decision_comment[0]
        })
    },
    markDecisionPending() {
      this.service
        .markDecisionPending(this.lookupParams)
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
        })
        .catch(this.errorHandler)
    },
    async markAsPaid() {
      await this.$confirm('Are you sure to mark this application as paid?', {
        type: 'warning'
      })
      this.service
        .markAsPaid(this.lookupParams)
        .then(() => {
          this.$message({
            message: 'Status has been changed',
            type: 'success'
          })
          this.fetchData()
        })
        .catch(this.errorHandler)
    },
    markApproved() {
      this.decisionService = this.service.markApproved
      this.dialogVisible = true
    },
    markUnsuccessful() {
      this.decisionService = this.service.markUnsuccessful
      this.dialogVisible = true
    },
    generateOtpCode() {
      if (!this.otpCodeApplicantId || this.otpCodeRequestLoading) {
        return
      }

      this.otpCodeRequestLoading = true
      User.createOTPCode({ id: this.otpCodeApplicantId })
        .then(response => {
          this.otpCode = response.data.code
          this.otpCodeExpiresAt = response.data.expires_at
        })
        .catch(error => {
          this.errorHandler(error)
        })
        .finally(() => {
          this.otpCodeRequestLoading = false
        })
    },
    getNavButtons() {
      return [
        {
          component: 'router-link',
          attrs: {
            to: { ...this.redirectRoute, query: this.$route.query }
          },
          condition: this.redirectRoute,
          children: [
            {
              component: 'el-button',
              innerText: 'Cancel'
            }
          ]
        },
        {
          component: 'el-button',
          attrs: {
            type: 'warning',
            disabled: ![
              'payment_pending',
              'payment_unsuccessful',
              'submitted'
            ].includes(this.form.status)
          },
          listeners: {
            click: this.markAsPaid
          },
          innerText: 'Mark as paid'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'primary',
            disabled: this.form.status !== 'payment_received'
          },
          listeners: {
            click: this.markDecisionPending
          },
          innerText: 'Start processing'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'success',
            disabled: this.form.status !== 'decision_pending'
          },
          listeners: {
            click: this.markApproved
          },
          innerText: 'Approve'
        },
        {
          component: 'el-button',
          attrs: {
            type: 'danger',
            disabled: this.form.status !== 'decision_pending'
          },
          listeners: {
            click: this.markUnsuccessful
          },
          innerText: 'Reject'
        }
      ]
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep .is-disabled [class$='inner'],
::v-deep .el-select__tags-text {
  color: #303133 !important;
}

::v-deep .el-select {
  &__caret {
    display: none;
  }
}
::v-deep .el-form-item__label {
  width: 100% !important;
  margin-bottom: 0.5rem;
  text-align: left;
}
::v-deep .el-form-item__content {
  margin-left: 0 !important;
}
.comment-error {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  padding-top: 4px;
  top: 100%;
  left: 0;
}
::v-deep .formset-wrapper .formset-wrapper {
  margin-left: 20px;
}
.otp-code-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.otp-code-group {
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
}

.generate-button {
  width: 80px;
}

.generate-button-expired::v-deep .el-input__inner {
  color: #909399;
  text-decoration: line-through;
}

.otp-code-expiry {
  margin-left: 8px;
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}
</style>
